import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { TableTabs } from '@/components/common/TableTabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { formatNumber } from '@/utils/number';
import { designationListQueryOptions } from '@/features/organization/api/organization.api';
import {
  dcrApprovalInfiniteQueryOptions,
  derApprovalInfiniteQueryOptions,
  expenseTypeQueryOptions,
  transportTypeQueryOptions,
} from '../api/billing.api';
import { DcrEntryDetailsModal } from '../components/DcrEntryDetailsModal';
import { BillingFilterPanel } from '../components/BillingFilterPanel';
import { getDcrQueueColumns } from '../components/DcrQueueColumns';
import { getDerQueueColumns } from '../components/DerQueueColumns';
import {
  DcrDraftsContext,
  DerDraftsContext,
  type DcrRowDraft,
} from '../components/queueDraftsContext';
import {
  BILLING_ENTRY_TABS,
  BILLING_ENTRY_TABS_LABEL,
  BILLING_SEARCH_PLACEHOLDER,
  QUEUE_FIT_CONTENT_COLUMN_IDS,
  type BillingEntryTab,
} from '../constants/billing.constants';
import { useApprovalFilters } from '../hooks/useApprovalFilters';
import { useDerBillDownload } from '../hooks/useDerBillDownload';
import {
  getApprovalStatusFilterField,
  getDesignationFilterField,
  getExpenseTypeFilterField,
  getMinAmountFilterField,
  getMinCostFilterField,
  getWorkTypeFilterField,
} from '../utils/billingFilterFields';
import { getClaimStatCards, getLimitStatCards } from '../utils/billingStatCards';
import { useApproveDcr, useApproveDer } from '../hooks/useApproveEntry';
import type { OfflineDcrRow, OfflineDerRow } from '../types/billing.types';
import { getAmountCap, getStartingTransportTypeId, type BillingMode } from '../utils/verifiability';

const BILLING_MODE: BillingMode = 'approve';

interface DcrDecision {
  row: OfflineDcrRow;
  accept: boolean;
}

interface DerDecision {
  row: OfflineDerRow;
  accept: boolean;
}

export function DcrDerApprovalPage() {
  const controller = useApprovalFilters('approval');
  const [tab, setTab] = useState<BillingEntryTab>('dcr');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { data: designations } = useQuery(designationListQueryOptions());

  const isDcrTab = tab === 'dcr';
  const dcrQuery = useInfiniteTableQuery(dcrApprovalInfiniteQueryOptions(controller.dcrParams));
  const derQuery = useInfiniteTableQuery(derApprovalInfiniteQueryOptions(controller.derParams));
  const dcrLimits = dcrQuery.firstPageMeta?.limits;
  const derLimits = derQuery.firstPageMeta?.limits;
  const dcrCount = dcrQuery.firstPageMeta?.total;
  const derCount = derQuery.firstPageMeta?.total;
  const { data: transportTypes, isLoading: transportTypesLoading } = useQuery(
    transportTypeQueryOptions(),
  );
  const { data: expenseTypes } = useQuery(expenseTypeQueryOptions());

  const tabsWithCounts = useMemo(() => {
    const countByTab: Record<BillingEntryTab, number | undefined> = {
      dcr: dcrCount,
      der: derCount,
    };

    return BILLING_ENTRY_TABS.map((entryTab) => {
      const count = countByTab[entryTab.id];
      return {
        ...entryTab,
        count,
        countTitle: count === undefined ? undefined : `${formatNumber(count)} matching the filters`,
      };
    });
  }, [dcrCount, derCount]);

  const approveDcr = useApproveDcr();
  const approveDer = useApproveDer();
  const { downloadingDetailId, downloadBill } = useDerBillDownload();

  const [dcrDrafts, setDcrDrafts] = useState<Record<number, DcrRowDraft>>({});
  const [derDrafts, setDerDrafts] = useState<Record<number, string>>({});
  const [dcrDecision, setDcrDecision] = useState<DcrDecision | null>(null);
  const [derDecision, setDerDecision] = useState<DerDecision | null>(null);
  const [detailsRecordId, setDetailsRecordId] = useState<number | null>(null);

  const handleDcrDraftChange = useCallback((recordId: number, patch: Partial<DcrRowDraft>) => {
    setDcrDrafts((prev) => ({
      ...prev,
      [recordId]: {
        approveFareAmount: patch.approveFareAmount ?? prev[recordId]?.approveFareAmount ?? '',
        transportTypeId:
          'transportTypeId' in patch ? patch.transportTypeId : prev[recordId]?.transportTypeId,
      },
    }));
  }, []);

  const handleDerDraftChange = useCallback((detailId: number, approveAmount: string) => {
    setDerDrafts((prev) => ({ ...prev, [detailId]: approveAmount }));
  }, []);

  const { search } = controller.dcrParams;
  const pendingDcrRecordId = approveDcr.isPending ? (approveDcr.variables?.recordId ?? null) : null;
  const dcrColumns = useMemo(() => {
    return getDcrQueueColumns({
      mode: BILLING_MODE,
      limits: dcrLimits,
      transportTypes,
      transportTypesLoading,
      onDecide: (row, accept) => setDcrDecision({ row, accept }),
      onOpenDetails: setDetailsRecordId,
      pendingRecordId: pendingDcrRecordId,
      searchText: search,
    });
  }, [dcrLimits, transportTypes, transportTypesLoading, pendingDcrRecordId, search]);

  const pendingDerDetailId = approveDer.isPending ? (approveDer.variables?.detailId ?? null) : null;
  const derColumns = useMemo(() => {
    return getDerQueueColumns({
      mode: BILLING_MODE,
      limits: derLimits,
      onDecide: (row, accept) => setDerDecision({ row, accept }),
      onDownload: (row) => void downloadBill(row),
      pendingDetailId: pendingDerDetailId,
      downloadingDetailId,
      searchText: search,
    });
  }, [derLimits, downloadBill, pendingDerDetailId, downloadingDetailId, search]);

  function confirmDcr() {
    if (!dcrDecision) return;

    const { row } = dcrDecision;
    const draft = dcrDrafts[row.recordId];
    const fareCap = getAmountCap(BILLING_MODE, row.fareAmount, row.approveFareAmount);
    const approveFareAmount = Number(draft?.approveFareAmount ?? fareCap.amount);
    const transportTypeId =
      draft?.transportTypeId ?? getStartingTransportTypeId(BILLING_MODE, row) ?? undefined;

    approveDcr.mutate(
      {
        recordId: dcrDecision.row.recordId,
        payload: dcrDecision.accept
          ? {
              accept: true,
              approveFareAmount,
              ...(transportTypeId && { transportTypeId }),
            }
          : { accept: false },
      },
      { onSettled: () => setDcrDecision(null) },
    );
  }

  function confirmDer() {
    if (!derDecision) return;

    const amountCap = getAmountCap(
      BILLING_MODE,
      derDecision.row.amount,
      derDecision.row.approveAmount,
    );
    const approveAmount = Number(derDrafts[derDecision.row.detailId] ?? amountCap.amount);
    const approveExpenseTypeId =
      derDecision.row.approveExpenseTypeId ?? derDecision.row.expenseTypeId ?? undefined;

    approveDer.mutate(
      {
        detailId: derDecision.row.detailId,
        payload: derDecision.accept
          ? {
              accept: true,
              approveAmount,
              ...(approveExpenseTypeId && { approveExpenseTypeId }),
            }
          : { accept: false },
      },
      { onSettled: () => setDerDecision(null) },
    );
  }

  const activeQuery = isDcrTab ? dcrQuery : derQuery;
  const dcrStats = [
    ...getClaimStatCards('dcr', dcrQuery.firstPageMeta),
    ...getLimitStatCards('dcr', dcrLimits),
  ];
  const derStats = [
    ...getClaimStatCards('der', derQuery.firstPageMeta),
    ...getLimitStatCards('der', derLimits),
  ];
  const activeStats = isDcrTab ? dcrStats : derStats;
  const activeTable = isDcrTab ? (
    <DcrDraftsContext.Provider value={{ drafts: dcrDrafts, onDraftChange: handleDcrDraftChange }}>
      <DataTable
        data={dcrQuery.rows}
        columns={dcrColumns}
        isLoading={dcrQuery.isLoading}
        className="lg:flex-1"
        stickyColumnId="name"
        fitContentColumnIds={QUEUE_FIT_CONTENT_COLUMN_IDS}
        emptyMessage="Nothing waiting on approval"
        emptyHint="Entries appear here once they have been verified."
        {...dcrQuery.loadMoreProps}
      />
    </DcrDraftsContext.Provider>
  ) : (
    <DerDraftsContext.Provider value={{ drafts: derDrafts, onDraftChange: handleDerDraftChange }}>
      <DataTable
        data={derQuery.rows}
        columns={derColumns}
        isLoading={derQuery.isLoading}
        className="lg:flex-1"
        stickyColumnId="name"
        fitContentColumnIds={QUEUE_FIT_CONTENT_COLUMN_IDS}
        emptyMessage="Nothing waiting on approval"
        emptyHint="Expenses appear here once they have been verified."
        {...derQuery.loadMoreProps}
      />
    </DerDraftsContext.Provider>
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="DCR & DER Approval"
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: BILLING_SEARCH_PLACEHOLDER,
        }}
        titleAddon={
          <DateRangeBadge dateFrom={controller.period.dateFrom} dateTo={controller.period.dateTo} />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <BillingFilterPanel
          filters={controller}
          fieldsBeforeGeo={[
            getApprovalStatusFilterField(),
            getDesignationFilterField(designations),
            isDcrTab
              ? getMinAmountFilterField('minFare', 'Fare (Tk), more than')
              : getMinCostFilterField(),
          ]}
          fieldsAfterGeo={[
            isDcrTab ? getWorkTypeFilterField() : getExpenseTypeFilterField(expenseTypes),
          ]}
        />
      </FilterPanelCollapse>

      <TableTabs
        tabs={tabsWithCounts}
        activeTabId={tab}
        onTabChange={setTab}
        ariaLabel={BILLING_ENTRY_TABS_LABEL}
      />

      {activeQuery.loadError ? (
        <ErrorState
          error={activeQuery.loadError}
          fallbackTitle="Could not load the approval queue"
          onRetry={() => void activeQuery.refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={activeQuery.isLoading}
            stats={activeStats}
            layoutCardCount={Math.max(dcrStats.length, derStats.length)}
          />

          {activeTable}
        </>
      )}

      <DcrEntryDetailsModal recordId={detailsRecordId} onClose={() => setDetailsRecordId(null)} />

      <ConfirmDialog
        open={dcrDecision !== null}
        onOpenChange={(open) => {
          if (!open) setDcrDecision(null);
        }}
        title={dcrDecision?.accept ? 'Approve this fare?' : 'Reject this entry?'}
        description={
          dcrDecision
            ? `${dcrDecision.row.name}'s entry from ${dcrDecision.row.workTypeCode ?? 'this visit'}.`
            : ''
        }
        confirmLabel={dcrDecision?.accept ? 'Approve' : 'Reject'}
        confirmVariant={dcrDecision?.accept ? 'default' : 'destructive'}
        isPending={approveDcr.isPending}
        pendingLabel="Saving"
        onConfirm={confirmDcr}
      />

      <ConfirmDialog
        open={derDecision !== null}
        onOpenChange={(open) => {
          if (!open) setDerDecision(null);
        }}
        title={derDecision?.accept ? 'Approve this expense?' : 'Reject this expense?'}
        description={
          derDecision
            ? `${derDecision.row.name}'s ${derDecision.row.expenseType ?? 'expense'}.`
            : ''
        }
        confirmLabel={derDecision?.accept ? 'Approve' : 'Reject'}
        confirmVariant={derDecision?.accept ? 'default' : 'destructive'}
        isPending={approveDer.isPending}
        pendingLabel="Saving"
        onConfirm={confirmDer}
      />
    </div>
  );
}
